import { Box, Checkbox, CircularProgress, Text, VStack } from '@chakra-ui/react'
import { OverridesContextProvider, type OverridesContextType, overrideText } from 'ui/lib/overrides'
import type { Language } from 'ecosystem'
import { useMemo } from 'react'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { FormProvider } from 'react-hook-form'
import { useCheckoutContext } from '../../hooks'
import { NshiftCheckoutWidgetComponent } from './NshiftCheckoutWidgetComponent'
import { CheckoutSectionHeading } from '../CheckoutSectionHeading'
import { type NshiftCheckoutProps } from './types'
import { nshiftCheckoutTranslations, type NshiftOverrides } from './translations'
import { NshiftAddress } from './NshiftAddress'
import { useNshiftCheckout } from './useNshiftCheckout'

const NshiftCheckoutComponent = ({ containerProps, onDataChange }: NshiftCheckoutProps) => {
  const overrides = useOverridesContext<keyof NshiftOverrides>()
  const { orderResponse, shippingConfig, summaryTotal, selectedCountryCode } = useCheckoutContext()

  const initialData = useMemo(() => {
    const savedDetails =
      orderResponse?.storefrontOrderResponse?.shippingInfo?.additionalInfo?.nshiftDetails
    if (!selectedCountryCode || savedDetails?.recipientAddress?.country) {
      return savedDetails
    }
    return {
      ...savedDetails,
      recipientAddress: {
        ...savedDetails?.recipientAddress,
        country: selectedCountryCode
      }
    }
  }, [
    orderResponse?.storefrontOrderResponse?.shippingInfo?.additionalInfo?.nshiftDetails,
    selectedCountryCode
  ])

  const {
    form,
    onChangeWidgetResult,
    onReadyWidget,
    deliveryOptionsData,
    isLoadingDeliveryOptions,
    isRememberOptedIn,
    setRememberOptedIn
  } = useNshiftCheckout({
    cartTotalAmount: summaryTotal,
    storefrontOrderNumber: orderResponse?.storefrontOrderResponse.orderNumber ?? '',
    initialData,
    onDataChange,
    availableCountries: shippingConfig?.nshiftOptions?.availableCountries
  })

  const renderDeliveryOptions = () => {
    return (
      <>
        {isLoadingDeliveryOptions && <CircularProgress isIndeterminate />}

        {!isLoadingDeliveryOptions && !deliveryOptionsData && (
          <Text color="text.mild">
            {overrideText('Inga leveransalternativ tillgängliga.', overrides?.noDeliveryOptions)}
          </Text>
        )}

        <Box display={!deliveryOptionsData || isLoadingDeliveryOptions ? 'none' : 'block'} w="100%">
          <NshiftCheckoutWidgetComponent
            onReady={onReadyWidget}
            resultCallback={onChangeWidgetResult}
          />
        </Box>
      </>
    )
  }

  return (
    <FormProvider {...form}>
      <VStack w="100%">
        <VStack w="100%" maxW="600px" gap={6} align="stretch" pt={6} {...containerProps}>
          <VStack w="100%" align="stretch">
            <CheckoutSectionHeading>
              {overrideText('Mottagaruppgifter', overrides?.recipientTitle)}
            </CheckoutSectionHeading>
            <NshiftAddress
              availableCountries={shippingConfig?.nshiftOptions?.availableCountries}
              isRequiredStreetAddress
            />

            {/* The shopper's explicit request to have these details kept on this
                device — nothing is stored until it is ticked. */}
            <Checkbox
              borderColor="primary"
              isChecked={isRememberOptedIn}
              mt={2}
              onChange={(event) => setRememberOptedIn(event.target.checked)}
              size="md"
            >
              {overrideText(
                'Kom ihåg mina uppgifter på den här enheten',
                overrides?.rememberOnDevice
              )}
            </Checkbox>
          </VStack>

          <VStack w="100%" align="stretch">
            <CheckoutSectionHeading>
              {overrideText('Leveransalternativ', overrides?.deliveryOptionsTitle)}
            </CheckoutSectionHeading>

            {renderDeliveryOptions()}
          </VStack>
        </VStack>
      </VStack>
    </FormProvider>
  )
}

export const NshiftCheckout = (props: NshiftCheckoutProps) => {
  const lang = document.documentElement.lang ?? 'sv'

  return (
    <OverridesContextProvider
      overrides={nshiftCheckoutTranslations[lang as Language] as unknown as OverridesContextType}
    >
      <NshiftCheckoutComponent {...props} />
    </OverridesContextProvider>
  )
}
